import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import PropTypes from 'prop-types'

import { Checkbox } from '../../../../components'

// A single touch target wrapping the checkbox and its label. Shown only when
// every person in planning has SSI excluded: checking it records the "don't show
// me again" preference and advances past this step. A <button> (rather than a
// <label>, which wouldn't forward clicks to the div-based Checkbox) so the whole
// row is one keyboard-focusable, clickable target with a hover state.
const Control = styled.button`
  display: inline-flex;
  align-items: center;
  column-gap: 12px;
  padding: 6px 8px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 15px;
  color: ${p => p.theme.mediumGray};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${p => p.theme.addButtonHoverColor};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

/**
 * "Don't show me this again" checkbox. Because activating it both records the
 * preference and moves the user forward, its click fires the caller's
 * `onConfirm` (persist the flag, then advance). `checked` reflects the persisted
 * user-settings flag so the box shows the current state if the step is revisited.
 *
 * `disabled` is an explicit prop rather than "no handler passed", which SSI
 * already uses to mean something else (record the preference without advancing).
 * The advice flow sets it while a chatbot response is streaming, since activating
 * this navigates.
 */
function DontShowAgain({ checked, onConfirm, disabled, className }) {
  const theme = useTheme()
  return (
    <Control
      type='button'
      theme={theme}
      className={className}
      disabled={disabled}
      onClick={onConfirm}>
      <Checkbox checked={checked} />
      Don't show me this again
    </Control>
  )
}

DontShowAgain.propTypes = {
  checked: PropTypes.bool,
  /** Called when the control is activated; should persist the flag and advance. */
  onConfirm: PropTypes.func.isRequired,
  /** Block the control — it advances the flow, so callers lock it when needed. */
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

export default DontShowAgain
